import { formatISO } from 'date-fns';

import { Duration } from './Duration';

export type DateTimeFormatOptions = {
  readonly year: number;
  // yearShortString: string;
  // yearLongString: string;
  readonly month: number;
  readonly monthShortString: string;
  readonly monthLongString: string;
  readonly day: number;
  // dayShortString: string;
  // dayLongString: string;
};

export type DateTimeFormatTemplate = (
  o: Partial<DateTimeFormatOptions>,
) => string;

type LocaleKey = string;
type TemplateKey = string;

//regex defined once and reused
//https://stackoverflow.com/a/43931246
//IMPORTANT: We get ISO 8601 strings from python with fractions of a second, which this regex supports
const iso8601TestRegex =
  /^(?:[1-9]\d{3}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1\d|2[0-8])|(?:0[13-9]|1[0-2])-(?:29|30)|(?:0[13578]|1[02])-31)|(?:[1-9]\d(?:0[48]|[2468][048]|[13579][26])|(?:[2468][048]|[13579][26])00)-02-29)T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,9})?(?:Z|[+-][01]\d:[0-5]\d)$/;

export class DateTime {
  private static templatesByLocale = new Map<
    LocaleKey,
    Map<TemplateKey, DateTimeFormatTemplate>
  >();

  #value: number; //UTC milliseconds since unix epoch (Jan 1, 1970, midnight)

  constructor(
    value: number, //UTC milliseconds since unix epoch (Jan 1, 1970, midnight)
  ) {
    if (Number.isSafeInteger(value) && value >= 0) {
      this.#value = value;
    } else {
      throw new Error(
        'DateTime must be constructed with positive integer of milliseconds',
      );
    }
  }

  //UTC milliseconds since unix epoch (Jan 1, 1970, midnight)
  public get value(): number {
    return this.#value;
  }

  public get localDate(): Date {
    return new Date(this.#value); //browser's local timezone
  }

  public get iso(): string {
    return formatISO(this.#value);
  }

  public format(
    options: {
      localeKey?: LocaleKey; //default is 'en'
      timeZone?: string; //default is browser's local time zone
    } & (
      | { templateKey: TemplateKey; template: never } //Prefer passing a templateKey so the template for the correct locale is looked up
      | { template: DateTimeFormatTemplate; templateKey: never }
    ), //If the template is same for all locales, the template can be passed
  ) {
    const localeKey = options.localeKey ?? 'en';
    const timeZone = options.timeZone;
    const template = options.templateKey
      ? (
          DateTime.templatesByLocale.get(localeKey) ||
          DateTime.templatesByLocale.get('en')
        )?.get(options.templateKey)
      : options.template;
    if (!template) {
      throw new Error('Could not find template');
    }
    return template(this.formatOptionsForLocale(localeKey, timeZone));
  }

  //TODO use or remove timeZone parameter
  public formatOptionsForLocale(
    localeKey: LocaleKey,
    //timeZone?: string,
  ): DateTimeFormatOptions {
    const d = new Date(this.#value); //local browser time (not considering timeZone)
    //Use date-fns to do this right.
    return {
      get year() {
        return d.getFullYear();
      },
      get month() {
        return d.getMonth() + 1; //getMonth() is zero based!!!!
      },
      get monthShortString() {
        const templatesForLocale =
          DateTime.templatesByLocale.get(localeKey) ??
          DateTime.templatesByLocale.get('en');
        const template = templatesForLocale
          ? templatesForLocale.get('monthShortString')
          : undefined;
        //Note: `this` is the returned DateTimeFormatOptions!
        return template ? template({ month: this.month }) : '';
      },
      get monthLongString() {
        const templatesForLocale =
          DateTime.templatesByLocale.get(localeKey) ??
          DateTime.templatesByLocale.get('en');
        const template = templatesForLocale
          ? templatesForLocale.get('monthLongString')
          : undefined;
        //Note: `this` is the returned DateTimeFormatOptions!
        return template ? template({ month: this.month }) : '';
      },
      get day() {
        return d.getDay();
      },
    };
  }

  public add(v: Duration): DateTime {
    return v.value === 0 ? this : new DateTime(this.value + v.value);
  }

  public sub(v: Duration): DateTime {
    return v.value === 0 ? this : new DateTime(this.value - v.value);
  }

  public diff(a: DateTime, b: DateTime): Duration {
    return new Duration(a.#value - b.#value);
  }

  public static fromIso(v: string) {
    if (DateTime.isValidIso(v)) {
      return new DateTime(new Date(v).valueOf());
    }
    throw new Error(`This is not a valid iso 8601 string: ${v}`);
  }

  public static isValidIso(v: string): boolean {
    iso8601TestRegex.lastIndex = 0; //IMPORTANT: reset index when reusing iso8601TestRegex
    return iso8601TestRegex.test(v);
    //Alternative using moment.js
    // try {
    //   return moment.utc(v, moment.ISO_8601).isValid();
    // } catch (e) {
    //   return false;
    // }
  }

  public static registerTemplatesForKey(
    localeKey: LocaleKey,
    key: TemplateKey,
    template: DateTimeFormatTemplate,
  ) {
    let templatesForLocale = DateTime.templatesByLocale.get(localeKey);
    if (!templatesForLocale) {
      templatesForLocale = new Map();
      DateTime.templatesByLocale.set(localeKey, templatesForLocale);
    }
    templatesForLocale.set(key, template);
  }
}

DateTime.registerTemplatesForKey('en', 'monthLongString', ({ month }) => {
  switch (month) {
    case 1:
      return 'January';
    case 2:
      return 'February';
    case 3:
      return 'March';
    case 4:
      return 'April';
    case 5:
      return 'May';
    case 6:
      return 'June';
    case 7:
      return 'July';
    case 8:
      return 'August';
    case 9:
      return 'September';
    case 10:
      return 'October';
    case 11:
      return 'November';
    case 12:
      return 'December';
    default:
      return '';
  }
});

DateTime.registerTemplatesForKey('en', 'monthShortString', ({ month }) => {
  switch (month) {
    case 1:
      return 'Jan';
    case 2:
      return 'Feb';
    case 3:
      return 'Mar';
    case 4:
      return 'Apr';
    case 5:
      return 'May';
    case 6:
      return 'Jun';
    case 7:
      return 'Jul';
    case 8:
      return 'Aug';
    case 9:
      return 'Sept';
    case 10:
      return 'Oct';
    case 11:
      return 'Nov';
    case 12:
      return 'Dec';
    default:
      return '';
  }
});

// const myDateTime = DateTime.fromIso(``); //Prefer using the factory which is specifically for say iso... and validates
// const myString: string = myDateTime.format(({year, month, day}) => `${month}, ${day}, ${year}`);
// console.log(myString);
// const myDateTime2 = new DateTime(0); //constructor (less preferred)
